(() => {
  if (window.MysteryStorefront) return;

  const SELECTORS = {
    form: '[data-mystery-form]',
    card: '[data-mystery-card]',
    submit: '[data-mystery-submit]',
    buttonText: '[data-mystery-button-text]',
    variant: '[data-mystery-variant-select]',
    size: '[data-mystery-size]',
    price: '[data-mystery-current-price]',
    image: '.mystery-box-card__image',
  };

  function getCartSurface() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  function getSubmitButtons(form) {
    return Array.from(form.querySelectorAll(SELECTORS.submit));
  }

  function setFormAvailability(form, available) {
    if (form.dataset.loading === 'true') return;

    form.dataset.selectedAvailable = String(available);
    getSubmitButtons(form).forEach((button) => {
      const buttonText = button.querySelector(SELECTORS.buttonText);
      button.disabled = !available;
      button.setAttribute('aria-disabled', String(!available));
      button.dataset.loading = 'false';

      if (buttonText) {
        buttonText.textContent = available ? button.dataset.defaultText || buttonText.textContent : 'Sold out';
      }
    });
  }

  function updateVariant(form) {
    const select = form.querySelector(SELECTORS.variant);

    if (!select) {
      setFormAvailability(form, form.dataset.selectedAvailable === 'true');
      return;
    }

    const option = select.selectedOptions[0];
    const card = form.closest(SELECTORS.card);
    const available = option?.dataset.available === 'true';
    const currentPrice = card?.querySelector(SELECTORS.price);
    const image = card?.querySelector(SELECTORS.image);

    if (currentPrice && option?.dataset.price) currentPrice.textContent = option.dataset.price;
    if (image && option?.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }

    card?.querySelectorAll(SELECTORS.size).forEach((button) => {
      const selected = button.dataset.variantId === option?.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
    });

    setFormAvailability(form, available);
  }

  function selectSize(event) {
    const button = event.target.closest(SELECTORS.size);
    if (!button || button.disabled || button.dataset.available !== 'true') return;

    const form = button.closest(SELECTORS.form);
    const select = form?.querySelector(SELECTORS.variant);
    const option = Array.from(select?.options || []).find((item) => item.value === button.dataset.variantId);
    if (!select || !option || option.disabled) return;

    select.value = button.dataset.variantId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function getSubmitter(event, form) {
    if (event.submitter?.matches?.(SELECTORS.submit)) return event.submitter;
    if (document.activeElement?.matches?.(SELECTORS.submit)) return document.activeElement;
    return form.querySelector(SELECTORS.submit);
  }

  const FAVORITE_TEAM_STORAGE_KEY = 'limiteduniq:favoriteTeam';
  const FAVORITE_TEAM_PROPERTY = 'Favorite Team';

  function getTeamModal() {
    return document.querySelector('[data-mystery-team-modal]');
  }

  function setTeamInputOnForm(form, teamName) {
    let input = form.querySelector('input[name="properties[' + FAVORITE_TEAM_PROPERTY + ']"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + FAVORITE_TEAM_PROPERTY + ']';
      form.appendChild(input);
    }
    input.value = teamName;
  }

  const SCROLL_LOCK_KEY = 'mystery-team-modal';

  // The theme already ships a shared, reference-counted scroll lock
  // (window.ThemeScrollLock, in global.js) that the cart drawer, menu drawer,
  // details-modal, and the Shiprocket fix script all coordinate through. This
  // modal previously rolled its own separate `body.style.position = 'fixed'`
  // lock, which could stomp on (or be stomped on by) the drawer's lock when
  // both were active around the same time, leaving the page stuck unscrollable
  // after closing. Delegating to ThemeScrollLock keeps everything in sync.
  function lockBodyScroll() {
    if (window.ThemeScrollLock) {
      window.ThemeScrollLock.lock(SCROLL_LOCK_KEY);
    } else {
      document.body.style.overflow = 'hidden';
    }
    document.body.classList.add('mystery-team-modal-open');
  }

  function unlockBodyScroll() {
    document.body.classList.remove('mystery-team-modal-open');
    if (window.ThemeScrollLock) {
      window.ThemeScrollLock.unlock(SCROLL_LOCK_KEY);
    } else {
      document.body.style.overflow = '';
    }
  }

  function openTeamModal(form, submitter) {
    const modal = getTeamModal();
    if (!modal) {
      // Modal markup unavailable: fall back to adding without asking rather than blocking the purchase.
      performAddToCart(form, submitter);
      return;
    }

    const input = modal.querySelector('[data-mystery-team-input]');

    modal._pendingForm = form;
    modal._pendingSubmitter = submitter;
    modal._returnFocus = submitter;

    if (input) {
      let saved = '';
      try {
        saved = window.localStorage.getItem(FAVORITE_TEAM_STORAGE_KEY) || '';
      } catch (e) {
        saved = '';
      }
      input.value = saved;
    }

    modal.hidden = false;
    lockBodyScroll();
    window.setTimeout(() => input?.focus(), 0);
  }

  function closeTeamModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    unlockBodyScroll();
    modal._returnFocus?.focus?.();
    modal._pendingForm = null;
    modal._pendingSubmitter = null;
    modal._returnFocus = null;
  }

  function confirmTeamModal(modal) {
    const input = modal.querySelector('[data-mystery-team-input]');
    const team = (input?.value || '').trim();

    if (team) {
      try {
        window.localStorage.setItem(FAVORITE_TEAM_STORAGE_KEY, team);
      } catch (e) {
        /* private mode / storage disabled: purchase still proceeds */
      }
    }

    const form = modal._pendingForm;
    const submitter = modal._pendingSubmitter;
    modal.hidden = true;
    unlockBodyScroll();
    modal._pendingForm = null;
    modal._pendingSubmitter = null;
    modal._returnFocus = null;

    if (form) {
      if (team) setTeamInputOnForm(form, team);
      performAddToCart(form, submitter);
    }
  }

  function bindTeamModal() {
    const modal = getTeamModal();
    if (!modal || modal.dataset.mysteryBound === 'true') return;
    modal.dataset.mysteryBound = 'true';

    modal.querySelectorAll('[data-mystery-team-cancel]').forEach((el) => {
      el.addEventListener('click', () => closeTeamModal(modal));
    });
    modal.querySelector('[data-mystery-team-confirm]')?.addEventListener('click', () => confirmTeamModal(modal));
    modal.querySelector('[data-mystery-team-input]')?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        confirmTeamModal(modal);
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) closeTeamModal(modal);
    });
  }

  function submitMysteryForm(event) {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    const form = event.currentTarget;
    if (form.dataset.loading === 'true') return;

    const submitter = getSubmitter(event, form);
    if (!submitter || submitter.disabled) return;

    openTeamModal(form, submitter);
  }

  // Third-party cart apps (Shiprocket Smart Cart, Razorpay COD Magic Checkout) commonly
  // attach their own click handlers directly to standard add-to-cart buttons, which can
  // run before a normal (bubble-phase) listener of ours and add the item straight to
  // their own cart UI, skipping our modal entirely. A capture-phase listener on
  // `document` is invoked while the event travels DOWN toward the target, before it
  // ever reaches listeners bound on the button itself — so this reliably wins
  // regardless of script load order or where the competing handler is attached.
  function interceptMysteryClick(event) {
    const button = event.target.closest?.(SELECTORS.submit);
    if (!button) return;

    const form = button.closest(SELECTORS.form);
    if (!form) return;
    if (button.disabled || form.dataset.loading === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    openTeamModal(form, button);
  }

  document.addEventListener('click', interceptMysteryClick, true);

  async function performAddToCart(form, submitter) {
    const card = form.closest(SELECTORS.card);
    const status = form.querySelector('[data-mystery-status]');
    const submitterText = submitter?.querySelector(SELECTORS.buttonText);
    const buyNow = submitter?.hasAttribute('data-mystery-buy-now');
    const cart = getCartSurface();
    let keepAddedState = false;

    form.dataset.loading = 'true';
    card?.classList.add('is-opening');
    getSubmitButtons(form).forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.dataset.loading = 'false';
    });
    submitter.dataset.loading = 'true';

    if (submitterText) submitterText.textContent = buyNow ? 'Processing...' : 'Adding...';
    if (status) status.textContent = buyNow ? 'Preparing checkout...' : 'Adding to cart...';

    try {
      const formData = new FormData(form);
      formData.delete('return_to');

      if (cart?.getSectionsToRender) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(submitter);
      }

      const response = await fetch(window.routes?.cart_add_url || form.action, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      let state = {};
      try {
        state = await response.json();
      } catch (error) {
        state = {};
      }

      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'This item could not be added. Please try again.');
      }

      card?.classList.remove('is-opening');

      // Buy Now used to hard-redirect straight to Shopify's own /checkout here,
      // bypassing whatever Shiprocket's app hooks into on a normal add-to-cart.
      // That's exactly why Add to Cart opened Shiprocket's faster checkout but
      // Buy Now fell through to the plain default checkout instead. Buy Now now
      // follows the identical path as Add to Cart (cart update event + render
      // cart contents) so Shiprocket treats both the same way.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'mystery-box',
          productVariantId: formData.get('id'),
          cartData: state,
        });
      }

      card?.classList.add('is-added');
      if (submitterText) submitterText.textContent = 'Added';
      if (status) status.textContent = 'Added to cart.';

      if (cart?.renderContents && state.sections) {
        cart.classList.remove('is-empty');
        cart.renderContents(state);
        keepAddedState = true;
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      }

      window.setTimeout(() => {
        card?.classList.remove('is-added');
        if (status) status.textContent = '';
        updateVariant(form);
      }, 1800);
    } catch (error) {
      card?.classList.remove('is-opening', 'is-added');
      card?.classList.add('has-error');
      if (status) status.textContent = error.message;
      window.setTimeout(() => card?.classList.remove('has-error'), 1200);
    } finally {
      form.dataset.loading = 'false';
      if (submitter) submitter.dataset.loading = 'false';
      if (!keepAddedState) updateVariant(form);
    }
  }

  function bind(root = document) {
    // Defensive recovery: if a section hot-reload (theme editor) or an interrupted
    // flow left the page scroll-locked while the modal isn't actually open, unlock it.
    const existingModal = getTeamModal();
    if ((!existingModal || existingModal.hidden) && document.body.classList.contains('mystery-team-modal-open')) {
      unlockBodyScroll();
    }

    bindTeamModal();

    root.querySelectorAll(SELECTORS.form).forEach((form) => {
      if (form.dataset.mysteryBound === 'true') return;
      form.dataset.mysteryBound = 'true';
      form.addEventListener('submit', submitMysteryForm);

      getSubmitButtons(form).forEach((button) => {
        const buttonText = button.querySelector(SELECTORS.buttonText);
        if (!button.dataset.defaultText && buttonText) button.dataset.defaultText = buttonText.textContent.trim();
      });

      form.querySelectorAll(SELECTORS.size).forEach((button) => {
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', String(button.classList.contains('is-selected')));
      });
      form.addEventListener('click', selectSize);
      form.querySelector(SELECTORS.variant)?.addEventListener('change', () => updateVariant(form));
      updateVariant(form);
    });

    root.querySelectorAll('[data-mystery-faq] details').forEach((details) => {
      if (details.dataset.mysteryBound === 'true') return;
      details.dataset.mysteryBound = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        details.parentElement.querySelectorAll('details[open]').forEach((item) => {
          if (item !== details) item.removeAttribute('open');
        });
      });
    });
  }

  window.MysteryStorefront = { bind };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(), { once: true });
  } else {
    bind();
  }
  document.addEventListener('shopify:section:load', (event) => bind(event.target));
})();
